import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import CommonProgress from "components/loader/commonProgress";
import RecaptchaComponent from "screens/login/v2/Recaptcha";
import { useState } from "react";

const getPositionStyles = (position, isMobile) => {
  switch (position) {
    case "top-right":
      return {
        top: 0,
        right: 0,
        transform: isMobile ? "translate(0%, 0%)" : "translate(-10%, 10%)",
      };
    case "top-left":
      return {
        top: 0,
        left: 0,
        transform: isMobile ? "translate(0%, 0%)" : "translate(10%, 10%)",
      };
    case "bottom-right":
      return {
        bottom: 0,
        right: 0,
        transform: isMobile ? "translate(0%, 0%)" : "translate(-10%, -10%)",
      };
    case "bottom-left":
      return {
        bottom: 0,
        left: 0,
        transform: isMobile ? "translate(0%, 0%)" : "translate(10%, -10%)",
      };
    default:
      return {}; // Default position (centered)
  }
};

const Confirmation = (props) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const i18n = useTranslation();
  const theme = useSelector((state) => state?.app?.theme);
  const {
    open = false,
    setOpen,
    callback = new Function(),
    message = "",
    note = "",
    title = `${i18n.t("appointments.ARE_YOU_SURE")}`,
    type = "",
    showCancel = true,
    microphoneInfoJsx = <></>,
    position = "center",
    cancelText = "",
    okText = "",
    closeCallback = new Function(),
    cross = true,
    extraButton = "",
    extraFunction = new Function(),
    loading = false,
    showExitButton = false,
    exitAction = new Function(),
    exitButtonLoading = false,
    buttonColor = "",
    cancelFunction = new Function(),
    needReCaptcha = false,
    exitText = "",
    jsx = null, // Property to render custom JSX content
  } = props;

  const [isVerifyCode, setIsVerifyCode] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const handleClose = () => {
    if (closeCallback) {
      closeCallback();
      setCaptchaToken("");
      setIsVerifyCode(false);
    }
    if (!loading) {
      setOpen(false);
    }
  };

  // If jsx is provided, render a blank canvas dialog
  // Extracted as local render functions: they close over the same props and state, and
  // are invoked rather than mounted as components, so element types and positions in the
  // tree are unchanged.
  const renderBlankCanvasDialog = () => (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="custom-dialog"
      sx={{
        minHeight: "400px",
        margin: isMobile ? "10px" : "0px",
        zIndex: 99999,
      }}
      PaperProps={{
        style: {
          position: "absolute",
          ...getPositionStyles(position, isMobile),
          borderRadius: "1vw",
          minWidth: isMobile ? "0" : "clamp(27vh, 27vw, 27vw)",
          minHeight: isMobile ? "0" : "auto",
        },
      }}
    >
      {cross && (
        <Box sx={{ position: "absolute", right: "6px" }}>
          <IconButton
            disabled={loading || exitButtonLoading}
            onClick={() => {
              if (
                cancelText === "common.STAY_LOGGED_IN" ||
                typeof cancelFunction === "function"
              ) {
                cancelFunction();
                setCaptchaToken("");
                setIsVerifyCode(false);
              }
              handleClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <DialogContent sx={{ p: 0 }}>{jsx}</DialogContent>
    </Dialog>
  );

  const renderCancelButton = () => (
    <>
  {type !== "unknown" && showCancel && (
    <Button
      fullWidth
      variant="outlined"
      size="large"
      onClick={() => {
        if (
          cancelText === "common.STAY_LOGGED_IN" ||
          typeof cancelFunction === "function"
        ) {
          cancelFunction();
        }
        {
          handleClose();
        }
      }}
      disabled={loading || exitButtonLoading}
    >
      {type == "cancelConfirmation"
        ? i18n.t("common.GO_BACK")
        : type == "appointmentCancellation"
        ? i18n.t("common.GO_BACK")
        : cancelText
        ? i18n.t(cancelText)
        : i18n.t("common.CANCEL")}
    </Button>
  )}
    </>
  );

  // Label ladder split in two: a 5-rung chain costs 1+2+3+4+5 on its own, and keeping it
  // whole would leave this at the threshold. Rung order and each comparison are unchanged,
  // including the loose == on the two type checks.
  const renderConfirmFallbackLabel = () =>
    okText ? i18n.t(okText) : extraButton ? extraButton : i18n.t("common.OK");

  const renderConfirmLabel = () =>
    loading ? (
      <CommonProgress />
    ) : type == "cancelConfirmation" ? (
      i18n.t("common.YES_LEAVE")
    ) : type == "appointmentCancellation" ? (
      i18n.t("common.YES_CANCEL")
    ) : (
      renderConfirmFallbackLabel()
    );

  const renderConfirmButton = () => (
  <Button
    variant="contained"
    fullWidth
    size="large"
    onClick={async () => {
      if (type == "unknown") {
        if (!loading) {
          setOpen(false);
        }
      } else {
        await callback(captchaToken);

        // dispatch(set_isDirty(false));
        if (!loading) {
          setOpen(false);
        }
      }
    }}
    color={buttonColor || "primary"}
    disabled={
      loading ||
      exitButtonLoading ||
      (needReCaptcha && captchaToken === "")
    }
    sx={{
      background: buttonColor && "#f54336",
      marginLeft: type === "redirect" && "0px !important",
      marginTop: type === "redirect" && "1vh !important",
    }}
  >
    {renderConfirmLabel()}
  </Button>
  );

  const renderExitButton = () => (
    <>
  {showExitButton && (
    <Button
      onClick={async () => {
        await exitAction();
      }}
      fullWidth
      size="large"
      variant="contained"
      color="error"
      disabled={loading || exitButtonLoading}
    >
      {exitButtonLoading ? (
        <CommonProgress />
      ) : (
        i18n.t(exitText) || i18n.t("common.EXIT")
      )}
    </Button>
  )}
    </>
  );

  const renderDialogActions = () => (
    <DialogActions
      className={type === "redirect" && `px-3`}
      sx={{
        marginBottom: "1vw",
        justifyContent: "space-evenly",
        display: type === "redirect" && "inline-table",
        ...(isMobile && {
          height: isMobile ? "0" : "50vh",
          marginBottom: "1vw",
          justifyContent: "flex-end",
        }),
      }}
    >
      {renderCancelButton()}
      {renderConfirmButton()}
      {renderExitButton()}
    </DialogActions>
  );

  if (jsx) {
    return renderBlankCanvasDialog();
  }

  // Original dialog for when jsx is not provided
  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (type == "cancelConfirmation" || type == "cancelConfirmationJotting") {
            new Function();
          } else {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          minHeight: "400px",
          margin: isMobile ? "10px" : "0px",
          zIndex: 99999,
        }}
        PaperProps={{
          style: {
            position: "absolute",
            ...getPositionStyles(position, isMobile),
            borderRadius: "1vw",
            minWidth: isMobile ? "0" : "clamp(27vh, 27vw, 27vw)",
            minHeight: isMobile ? "0" : "auto",
          },
        }}
      >
        <Box sx={{ margin: "0.3vw 0.6vw -1.5vw auto !important" }}>
          {cross && (
            <IconButton
              disabled={loading || exitButtonLoading}
              onClick={() => {
                if (
                  cancelText === "common.STAY_LOGGED_IN" ||
                  typeof cancelFunction === "function"
                ) {
                  cancelFunction();
                  setCaptchaToken("");
                  setIsVerifyCode(false);
                }
                {
                  handleClose();
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <DialogTitle
          sx={{
            textAlign: "center",
            paddingTop: !cross ? "24px" : "0",
          }}
          id="alert-dialog-title"
        >
          {title}
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
          }}
        >
          <DialogContentText id="alert-dialog-description">
            {type == "cancelConfirmation"
              ? `${i18n.t(
                  "common.YOU_HAVE_SOME_UNSAVED_WORK_DO_YOU_STILL_WANT_TO_LEAVE_THE_PAGE"
                )} ?`
              : message}
            <Box>{note && note}</Box>
          </DialogContentText>
          {microphoneInfoJsx}
        </DialogContent>
        {needReCaptcha && (
          <center className="recaptcha-container">
            <RecaptchaComponent
              light={theme === "light"}
              setIsVerifyCode={setIsVerifyCode}
              setCaptchaToken={setCaptchaToken}
              action={"PASSWORD_RESET"}
            />
          </center>
        )}
        {renderDialogActions()}
      </Dialog>
    </>
  );
};
export default Confirmation;
